import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { brandColorPalette } from 'theme/theme';
import { ChartView } from './components/ChartView';

// Default chart data using the palette
const getDefaultChartData = () => [
  { label: 'Item 1', value: 30, color: brandColorPalette[0] },
  { label: 'Item 2', value: 50, color: brandColorPalette[1] },
  { label: 'Item 3', value: 20, color: brandColorPalette[2] },
];

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    chart: {
      insertChart: () => ReturnType;
    };
  }
}

export interface ChartData {
  label: string;
  value: number;
  color: string;
}

// Para múltiplas séries
export interface ChartSeries {
  name: string;
  color: string;
}

export interface MultipleSeriesChartData {
  series: ChartSeries[];
  categories: string[];
  values: number[][]; // values[serieIndex][categoriaIndex] = valor
}

export type ChartMode = 'simple' | 'multiple';

export type PieStyle = 'simple' | '3d' | 'donut';

export type PieSliceText = 'value' | 'percentage';

export interface ChartConfig {
  type: 'pie' | 'column' | 'bar' | 'line';
  title?: string;
  mode?: ChartMode; // Apenas para column e bar
  data: ChartData[]; // Para modo simples e pie
  multipleSeriesData?: MultipleSeriesChartData; // Para modo múltiplas séries
  pieStyle?: PieStyle; // Para gráficos de pizza: 'simple', '3d', 'donut'
  pieSliceText?: PieSliceText; // Para gráficos de pizza: 'value' ou 'percentage'
  imagePath?: string;
}

export const Chart = Node.create({
  name: 'chart',

  group: 'block',

  defining: true,

  isolating: true,

  addAttributes() {
    return {
      config: {
        default: {
          type: 'pie',
          data: getDefaultChartData(),
        } as ChartConfig,
        parseHTML: (element) => {
          const configAttr = element.getAttribute('data-config');
          return configAttr ? JSON.parse(configAttr) : undefined;
        },
        renderHTML: (attributes) => ({
          'data-config': JSON.stringify(attributes.config),
        }),
      },
      imagePath: {
        default: undefined,
        parseHTML: (element) => element.getAttribute('data-image-path'),
        renderHTML: (attributes) => ({
          'data-image-path': attributes.imagePath,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `div[data-type="${this.name}"]`,
      },
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    const config = node.attrs.config as ChartConfig;
    const imagePath = node.attrs.imagePath;

    // Se temos uma imagem salva, renderizar a imagem
    if (imagePath) {
      return [
        'div',
        mergeAttributes(HTMLAttributes, {
          'data-type': this.name,
          class: 'chart-container',
        }),
        [
          'img',
          {
            src: imagePath,
            alt: config.title || 'Gráfico',
          },
        ],
      ];
    }

    // Senão, renderizar container vazio (será preenchido pelo React)
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': this.name,
        class: 'chart-container',
      }),
      '',
    ];
  },

  addCommands() {
    return {
      insertChart:
        () =>
        ({ state, chain }) => {
          const position = state.selection.$from.start();

          return chain()
            .insertContent({
              type: this.name,
              attrs: {
                config: {
                  type: 'pie',
                  data: getDefaultChartData(),
                },
              },
            })
            .focus(position)
            .run();
        },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ChartView);
  },
});

export default Chart;
